import { useEffect } from "react";
import { LocalizedLink as Link } from "../components/LocalizedLink";
import { useLanguage } from "../context/LanguageContext";
import celulasmadre from "../assets/treatments/celulasmadre.webp";

const CONTENT = {
    es: {
        breadcrumbLabel: "Células Madre Mesenquimales",
        mainTitle: "🧬 Células Madre Mesenquimales (MSC)",
        tagline: "🧪 ESTUDIOS DE LABORATORIO PARA CÉLULAS COMPLETAMENTE GRATIS AL HACER DEPOSITO",
        description: "Estas células son reconocidas por su capacidad regenerativa, antiinflamatoria e inmunomoduladora. Apoyando la recuperación de tejidos, disminuyendo procesos inflamatorios y favoreciendo una mejor calidad de vida en distintos padecimientos musculoesqueléticos, neurológicos, autoinmunes y de desgaste celular.",
        disclaimer: "La cantidad de células aplicadas puede variar según el estado de salud del paciente.",
        packages: [
            {
                title: "🌱 10 Millones de Células Madre Mesenquimales",
                ideal: "Pacientes que buscan un enfoque preventivo, regeneración inicial o tratamiento de padecimientos leves a moderados. Así mismo para quienes buscan beneficios estéticos.",
                benefits: [
                    "Disminución de inflamación y dolor.",
                    "Apoyo en la regeneración de tejidos.",
                    "Mejora en movilidad y recuperación física.",
                    "Fortalecimiento de procesos naturales de reparación celular.",
                    "Recuperación más rápida después de lesiones leves.",
                ],
                indications: [
                    "Lesiones deportivas leves.",
                    "Dolor articular inicial.",
                    "Tendinitis o inflamación muscular.",
                    "Fatiga crónica leve.",
                    "Recuperación postoperatoria básica.",
                    "Programa antiaging preventivo.",
                ],
            },
            {
                title: "⚡ 25 Millones de Células Madre Mesenquimales",
                ideal: "Pacientes con procesos inflamatorios moderados o enfermedades degenerativas en etapas iniciales a intermedias.",
                benefits: [
                    "Mayor efecto antiinflamatorio y regenerativo.",
                    "Mejora funcional en articulaciones y tejidos.",
                    "Disminución del dolor crónico.",
                    "Estimulación más profunda de reparación celular.",
                    "Apoyo al sistema inmunológico.",
                ],
                indications: [
                    "Osteoartritis moderada.",
                    "Lesiones ortopédicas recurrentes.",
                    "Hernias discales (no elimina hernias).",
                    "Lesiones de cartílago.",
                    "Diabetes o prediabetes (en control).",
                    "Enfermedades autoinmunes leves.",
                    "Recuperación deportiva avanzada.",
                    "Fatiga crónica y desgaste físico.",
                ],
            },
            {
                title: "💪 50 Millones de Células Madre Mesenquimales",
                ideal: "Pacientes con padecimientos degenerativos avanzados, inflamación crónica o mayor necesidad de regeneración.",
                benefits: [
                    "Potente acción antiinflamatoria sistémica.",
                    "Regeneración celular avanzada.",
                    "Mejoría significativa en movilidad y calidad de vida.",
                    "Apoyo en procesos neurológicos y autoinmunes.",
                    "Mayor capacidad de reparación tisular.",
                ],
                indications: [
                    "Osteoartritis avanzada.",
                    "Enfermedades autoinmunes moderadas.",
                    "Lesiones severas de columna y articulaciones.",
                    "Diabetes tipo I y II.",
                    "Neuropatías y daño nervioso.",
                    "Recuperación posterior a cirugías complejas.",
                    "Fibromialgia y dolor crónico.",
                    "Procesos de desgaste asociados a la edad.",
                ],
            },
            {
                title: "🚀 100 Millones de Células Madre Mesenquimales",
                ideal: "Pacientes con condiciones complejas, sistémicas o degenerativas avanzadas que requieren un protocolo celular de alta concentración.",
                benefits: [
                    "Máxima acción regenerativa y antiinflamatoria.",
                    "Apoyo integral en múltiples sistemas del organismo.",
                    "Diabetes tipo I y II.",
                    "Mayor modulación inmunológica.",
                    "Potencial mejora funcional en enfermedades crónicas complejas.",
                    "Optimización de recuperación y calidad de vida.",
                ],
                indications: [
                    "Enfermedades neurodegenerativas.",
                    "Osteoartritis severa y daño articular avanzado.",
                    "Enfermedades autoinmunes complejas.",
                    "Secuelas inflamatorias crónicas.",
                    "Recuperación celular integral y protocolos de longevidad avanzada.",
                    "Daño tisular severo o multisistémico.",
                    "Pacientes que requieren terapia regenerativa intensa.",
                ],
            },
        ],
        importantNoteTitle: "Nota Importante",
        importantNoteDescription: "La cantidad de células recomendada puede variar según la condición clínica, edad, antecedentes médicos y objetivos terapéuticos de cada paciente. Los resultados pueden variar individualmente.",
        importantNoteContact: "Para más información o una valoración médica agenda una cita con nosotros.",
        sidebarTitle: "🔬 Apoyo regenerativo completo",
        sidebarDescription: "Nuestro tratamiento con células madre mesenquimales se enfoca en mejorar la función articular, la recuperación de tejidos y el equilibrio inmunológico en pacientes con distintos grados de afección.",
        ctaPrimary: "Agendar valoración",
        ctaSecondary: "Volver a tratamientos",
        idealFor: "Ideal para:",
        benefits: "✅ Principales beneficios:",
        indications: "📌 Indicadas principalmente para:",
    },
    en: {
        "breadcrumbLabel": "Mesenchymal Stem Cells",
        "mainTitle": "🧬 Mesenchymal Stem Cells (MSC)",
        "tagline": "🧪 COMPLIMENTARY CELL LABORATORY TESTING WITH YOUR DEPOSIT",
        "description": "These cells are known for their regenerative, anti-inflammatory, and immunomodulatory properties. They support tissue repair, help reduce inflammatory processes, and promote an improved quality of life in various musculoskeletal, neurological, autoimmune, and cellular‑degeneration conditions.",
        "disclaimer": "The number of cells administered may vary depending on the patient’s health status.",
        "packages": [
            {
                "title": "🌱 10 Million Mesenchymal Stem Cells",
                "ideal": "Ideal for patients seeking a preventive approach, early-stage regeneration, or treatment for mild to moderate conditions. Also suitable for those pursuing aesthetic benefits.",
                "benefits": [
                    "Reduced inflammation and pain.",
                    "Support for tissue regeneration.",
                    "Improved mobility and physical recovery.",
                    "Enhanced natural cellular repair processes.",
                    "Faster recovery after minor injuries."
                ],
                "indications": [
                    "Mild sports injuries.",
                    "Early joint pain.",
                    "Tendinitis or muscle inflammation.",
                    "Mild chronic fatigue.",
                    "Basic postoperative recovery.",
                    "Preventive anti-aging programs."
                ]
            },
            {
                "title": "⚡ 25 Million Mesenchymal Stem Cells",
                "ideal": "Designed for patients with moderate inflammatory processes or degenerative conditions in early to mid stages.",
                "benefits": [
                    "Stronger anti-inflammatory and regenerative effects.",
                    "Functional improvement in joints and tissues.",
                    "Reduction of chronic pain.",
                    "Deeper stimulation of cellular repair.",
                    "Support for immune system balance."
                ],
                "indications": [
                    "Moderate osteoarthritis.",
                    "Recurrent orthopedic injuries.",
                    "Disc herniations (does not eliminate hernias).",
                    "Cartilage injuries.",
                    "Controlled diabetes or prediabetes.",
                    "Mild autoimmune conditions.",
                    "Advanced sports recovery.",
                    "Chronic fatigue and physical wear."
                ]
            },
            {
                "title": "💪 50 Million Mesenchymal Stem Cells",
                "ideal": "Recommended for patients with advanced degenerative conditions, chronic inflammation, or greater regenerative needs.",
                "benefits": [
                    "Powerful systemic anti-inflammatory action.",
                    "Advanced cellular regeneration.",
                    "Significant improvement in mobility and quality of life.",
                    "Support for neurological and autoimmune processes.",
                    "Greater tissue repair capacity."
                ],
                "indications": [
                    "Advanced osteoarthritis.",
                    "Moderate autoimmune diseases.",
                    "Severe spine and joint injuries.",
                    "Type I and Type II diabetes.",
                    "Neuropathies and nerve damage.",
                    "Recovery after complex surgeries.",
                    "Fibromyalgia and chronic pain.",
                    "Age-related degenerative processes."
                ]
            },
            {
                "title": "🚀 100 Million Mesenchymal Stem Cells",
                "ideal": "For patients with complex, systemic, or advanced degenerative conditions requiring a high‑concentration cellular protocol.",
                "benefits": [
                    "Maximum regenerative and anti-inflammatory action.",
                    "Comprehensive support across multiple body systems.",
                    "Type I and Type II diabetes.",
                    "Enhanced immune modulation.",
                    "Potential functional improvement in complex chronic conditions.",
                    "Optimized recovery and quality of life."
                ],
                "indications": [
                    "Neurodegenerative diseases.",
                    "Severe osteoarthritis and advanced joint damage.",
                    "Complex autoimmune disorders.",
                    "Chronic inflammatory sequelae.",
                    "Comprehensive cellular recovery and advanced longevity protocols.",
                    "Severe or multisystem tissue damage.",
                    "Patients requiring intensive regenerative therapy."
                ]
            }
        ],
        "importantNoteTitle": "Important Note",
        "importantNoteDescription": "The recommended cell quantity may vary based on clinical condition, age, medical history, and each patient’s therapeutic goals. Individual results may vary.",
        "importantNoteContact": "For more information or a medical evaluation, schedule an appointment with our team.",
        "sidebarTitle": "🔬 Comprehensive Regenerative Support",
        "sidebarDescription": "Our mesenchymal stem cell therapy focuses on improving joint function, enhancing tissue recovery, and supporting immune balance in patients with varying levels of impairment.",
        "ctaPrimary": "Schedule Evaluation",
        "ctaSecondary": "Back to Treatments",
        "idealFor": "Ideal for:",
        "benefits": "✅ Key Benefits:",
        "indications": "📌 Primarily Indicated For:"
    }
};

export default function CelulasMadre() {
    const { lang } = useLanguage();
    const content = CONTENT[lang] || CONTENT.es;

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    return (
        <section id="tratamientos" className="scroll-mt-24 py-8">
            <div className="max-w-8xl mx-auto px-4 space-y-8">
                <nav className="text-sm text-gray-500">
                    <Link to="/" className="hover:underline">
                        Inicio
                    </Link>
                    <span className="mx-2">/</span>
                    <Link to="/tratamientos" className="hover:underline">
                        Tratamientos
                    </Link>
                    <span className="mx-2">/</span>
                    <span className="text-gray-700 font-semibold">{content.breadcrumbLabel}</span>
                </nav>

                <article className="section-surface overflow-hidden shadow-soft">
                    <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
                        <div className="p-6 md:p-8 space-y-6">
                            <div className="inline-flex items-center gap-3 rounded-full bg-cyan-50 px-4 py-2 text-sm font-bold text-turquoise shadow-sm">
                                {content.tagline}
                            </div>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">
                                    {content.mainTitle}
                                </h1>
                                <p className="mt-4 text-lg leading-relaxed text-slate-700">
                                    {content.description}
                                </p>
                                <p className="mt-4 text-slate-600 leading-relaxed">
                                    {content.disclaimer}
                                </p>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                                {content.packages.map((item) => (
                                    <article key={item.title} className="rounded-3xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
                                        <h2 className="text-xl font-bold text-slate-900">{item.title}</h2>
                                        <div className="mt-3 rounded-2xl bg-white p-4 text-slate-700 shadow-sm">
                                            <p className="font-semibold">{content.idealFor}</p>
                                            <p className="mt-2 text-sm leading-relaxed">{item.ideal}</p>
                                        </div>
                                        <div className="mt-4">
                                            <p className="font-semibold text-slate-900">{content.benefits}</p>
                                            <ul className="mt-3 space-y-2 list-disc list-inside text-slate-700 text-base lg:text-sm">
                                                {item.benefits.map((benefit) => (
                                                    <li key={benefit}>{benefit}</li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="mt-4">
                                            <p className="font-semibold text-slate-900">{content.indications}</p>
                                            <ul className="mt-3 space-y-2 list-disc list-inside text-slate-700 text-base lg:text-sm">
                                                {item.indications.map((indication) => (
                                                    <li key={indication}>{indication}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </article>
                                ))}
                            </div>

                            <div className="rounded-3xl border border-cyan-200 bg-cyan-50 p-5 text-slate-800 shadow-sm">
                                <p className="font-semibold">{content.importantNoteTitle}</p>
                                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                                    {content.importantNoteDescription}
                                </p>
                                <p className="mt-3 text-sm leading-relaxed text-slate-700">
                                    {content.importantNoteContact}
                                </p>
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <Link
                                    to="/reservar"
                                    className="btn btn--primary btn--shadow px-6 py-3"
                                >
                                    📅 {content.ctaPrimary}
                                </Link>
                                <Link
                                    to="/tratamientos"
                                    className="inline-flex items-center justify-center rounded-xl border border-cyan-200 bg-white px-6 py-3 font-semibold text-turquoise transition hover:bg-cyan-50"
                                >
                                    ↩️ {content.ctaSecondary}
                                </Link>
                            </div>
                        </div>

                        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-900 p-4 md:p-6">
                            <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.35),_transparent_35%)]" />
                            <div className="relative z-10 rounded-3xl bg-slate-950/90 p-4 shadow-xl">
                                <img
                                    src={celulasmadre}
                                    alt="Células Madre Mesenquimales"
                                    className="w-full rounded-3xl object-cover"
                                />
                                <div className="mt-5 rounded-3xl bg-white/90 p-5 text-slate-900 shadow-sm">
                                    <h2 className="text-xl font-semibold">{content.sidebarTitle}</h2>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-700">
                                        {content.sidebarDescription}
                                    </p>
                                </div>
                                <Link
                                    to="/reservar"
                                    className="btn btn--primary btn--shadow px-6 py-3"
                                >
                                    📅 {content.ctaPrimary}
                                </Link>
                                <Link
                                    to="/tratamientos"
                                    className="inline-flex items-center justify-center rounded-xl border border-cyan-200 bg-white px-6 py-3 font-semibold text-turquoise transition hover:bg-cyan-50"
                                >
                                    ↩️ {content.ctaSecondary}
                                </Link>
                            </div>
                        </div>
                    </div>
                </article>
            </div>
        </section>
    );
}
